/**
 * The skill tree.
 *
 * Nodes are built once from the data and then only have classes and text
 * toggled. Prerequisite connectors are drawn when the requirement sits
 * directly above; otherwise the node states its requirement in words.
 */

import {
  BRANCH_NAMES,
  BRANCH_ORDER,
  SKILLS,
  SKILLS_BY_ID,
  type SkillDef,
} from '../../data/skills.ts';
import { skillAvailable } from '../../sim/formulas.ts';
import type { GameState } from '../../sim/state.ts';
import { byId, setText } from '../dom.ts';
import { formatNumber } from '../format.ts';

interface NodeView {
  def: SkillDef;
  button: HTMLButtonElement;
  cost: HTMLElement;
  req: HTMLElement;
}

export interface SkillPanel {
  update(state: GameState): void;
}

export function createSkillPanel(onBuy: (id: string) => void): SkillPanel {
  const tree = byId('tree');
  const gmLabel = byId('skills-gm');
  const nodes: NodeView[] = [];

  for (const branch of BRANCH_ORDER) {
    const column = document.createElement('div');
    column.className = 'branch';

    const heading = document.createElement('div');
    heading.className = 'branch__name';
    heading.textContent = BRANCH_NAMES[branch];
    column.append(heading);

    const inBranch = SKILLS.filter((s) => s.branch === branch);
    inBranch.forEach((def, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'node';

      // Connector only when the prerequisite is the node directly above.
      const previous = inBranch[i - 1];
      if (def.req !== undefined && previous !== undefined && previous.id === def.req) {
        button.classList.add('has-link');
      }

      const head = document.createElement('div');
      head.className = 'node__head';
      const name = document.createElement('span');
      name.className = 'node__name';
      name.textContent = def.name;
      const cost = document.createElement('span');
      cost.className = 'node__cost';
      cost.textContent = `${def.cost} ideas`;
      head.append(name, cost);

      const effect = document.createElement('div');
      effect.className = 'node__effect';
      effect.textContent = def.effect;

      const blurb = document.createElement('div');
      blurb.className = 'node__blurb';
      blurb.textContent = def.blurb;

      const req = document.createElement('div');
      req.className = 'node__req';
      req.hidden = true;
      if (def.req !== undefined) {
        req.textContent = `Requires ${SKILLS_BY_ID[def.req]?.name ?? def.req}`;
      }

      button.append(head, effect, blurb, req);
      button.addEventListener('click', () => onBuy(def.id));
      column.append(button);
      nodes.push({ def, button, cost, req });
    });

    tree.append(column);
  }

  function update(state: GameState): void {
    setText(gmLabel, formatNumber(state.greyMatter, state.settings.numberFormat));

    for (const node of nodes) {
      const owned = (state.skills[node.def.id] ?? 0) > 0;
      const available = skillAvailable(state, node.def.id);
      const affordable = state.greyMatter.gte(node.def.cost);

      node.button.classList.toggle('is-owned', owned);
      node.button.classList.toggle('is-locked', !owned && !available);
      node.button.disabled = owned || !available || !affordable;

      setText(node.cost, owned ? 'Installed' : `${node.def.cost} ideas`);
      const showReq = !owned && !available;
      if (node.req.hidden === showReq) node.req.hidden = !showReq;
    }
  }

  return { update };
}
